/*
 * focusPanelConfig.ts
 * Declarative config of the focus panel rows. Each row counts a
 * specific, live (not period bound) subset of a features current
 * data and links to the matching filtered list view.
 */

// Shape of a single status row fetched for the focus panel context
export type FeatureStatusType = {
    id: number;
    status_condition: string;
    status_name?: string;
    is_checked?: boolean;
    expected_delivery_date?: string | null;
};

// Data fetched once and shared by every focus panel row
export interface FocusPanelContext {
    job: FeatureStatusType[];
    proposal: FeatureStatusType[];
    purchase: FeatureStatusType[];
    invoice: FeatureStatusType[];
    taskCount: number;
    userHash?: string;
    todayIso: string;
}

export interface FocusPanelItemConfig {
    key: string;
    label: string;
    // Feature required to show the row, empty for rows which are always available
    requiredFeature: string;
    count: (context: FocusPanelContext) => number;
    route: string;
    params: (context: FocusPanelContext) => Record<string, string>;
}

export const focusPanelItems: FocusPanelItemConfig[] = [
    {
        key: 'proposalsExpired',
        label: 'dashboard.todos.proposal.proposals_expired',
        requiredFeature: 'proposals',
        count: (context) => context.proposal.filter(item => item.status_condition === 'expired').length,
        route: '/proposals',
        params: () => ({ status: 'expired' }),
    },
    {
        key: 'purchasesToCheck',
        label: 'dashboard.todos.purchase.purchases_to_check',
        requiredFeature: 'purchases',
        count: (context) => context.purchase.filter(item => item.status_condition === 'open' && !item.is_checked).length,
        route: '/purchases',
        params: () => ({ status: 'open', is_checked: 'false' }),
    },
    {
        key: 'purchasesToReportDelivered',
        label: 'dashboard.todos.purchase.purchases_to_report_delivered',
        requiredFeature: 'purchases',
        count: (context) => context.purchase.filter(item => (
            item.status_condition === 'open' && !!item.expected_delivery_date && item.expected_delivery_date.slice(0, 10) <= context.todayIso
        )).length,
        route: '/purchases',
        params: () => ({ status: 'open' }),
    },
    {
        key: 'jobsToBeScheduled',
        label: 'dashboard.todos.job.jobs_to_be_scheduled',
        requiredFeature: 'jobs',
        count: (context) => context.job.filter(item => ['job_created', 'to_schedule'].includes(item.status_condition)).length,
        route: '/jobs',
        params: () => ({ status_condition: 'job_created' }),
    },
    {
        key: 'jobsToReschedule',
        label: 'dashboard.todos.job.jobs_to_reschedule',
        requiredFeature: 'jobs',
        count: (context) => context.job.filter(item => item.status_condition === 'incomplete').length,
        route: '/jobs',
        params: () => ({ status_condition: 'incomplete' }),
    },
    {
        key: 'workordersToVerify',
        label: 'dashboard.todos.job.workorders_to_verify',
        requiredFeature: 'jobs',
        count: (context) => context.job.filter(item => item.status_condition === 'completed').length,
        route: '/jobs',
        params: () => ({ status_condition: 'completed' }),
    },
    {
        key: 'invoicesOverdue',
        label: 'dashboard.todos.invoice.invoice_reminders_to_be_sent',
        requiredFeature: 'invoices',
        count: (context) => context.invoice.filter(item => item.status_condition === 'overdue').length,
        route: '/invoices',
        params: () => ({ status: 'overdue' }),
    },
    {
        key: 'tasksToFollowUp',
        label: 'dashboard.todos.task.tasks_to_follow_up',
        requiredFeature: '',
        count: (context) => context.taskCount,
        route: '/tasks',
        params: (context) => ({ assignee_hash: context.userHash || '', due_on_before: context.todayIso, checked: 'false' }),
    },
];
